import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseLocalDate, isOverdue } from '../utils/dates';
import { useApp } from '../context/AppContext';
import TaskCard from '../components/tasks/TaskCard';
import TaskForm from '../components/tasks/TaskForm';
import TaskDetailModal from '../components/tasks/TaskDetailModal';
import EmptyState from '../components/common/EmptyState';

const CATEGORIES = ['all', 'hvac', 'plumbing', 'electrical', 'exterior', 'appliance', 'automotive', 'general'];

const Tasks = () => {
  const { tasks, fetchTasks, fetchAppliances, loading } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  useEffect(() => { fetchTasks(); fetchAppliances(); }, [fetchTasks, fetchAppliances]);

  const handleEdit = (task) => { setEditingTask(task); setShowForm(true); };
  const handleClose = () => { setShowForm(false); setEditingTask(null); };

  const overdueCount = tasks.filter((t) => isOverdue(parseLocalDate(t.nextDue))).length;

  const filtered = tasks
    .filter((t) => filter === 'all' || t.category === filter)
    .filter((t) => !showOverdueOnly || isOverdue(parseLocalDate(t.nextDue)))
    .sort((a, b) => {
      const aOver = isOverdue(parseLocalDate(a.nextDue));
      const bOver = isOverdue(parseLocalDate(b.nextDue));
      if (aOver !== bOver) return aOver ? -1 : 1;
      return parseLocalDate(a.nextDue) - parseLocalDate(b.nextDue);
    });

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Maintenance Tasks</h1>
          {overdueCount > 0 && (
            <p className="page-subtitle" style={{ color: 'var(--danger)' }}>
              {overdueCount} overdue
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to="/tasks/suggestions" className="btn btn-secondary">✨ AI Suggestions</Link>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Task</button>
        </div>
      </div>

      <div className="filter-bar">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`filter-btn${filter === cat ? ' active' : ''}`}
            onClick={() => setFilter(cat)}
          >
            {cat}
          </button>
        ))}
        {overdueCount > 0 && (
          <button
            className={`filter-btn${showOverdueOnly ? ' active' : ''}`}
            style={showOverdueOnly ? {} : { color: 'var(--danger)', borderColor: 'var(--danger)' }}
            onClick={() => setShowOverdueOnly((v) => !v)}
          >
            ⚠ Overdue ({overdueCount})
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        filter === 'all' && !showOverdueOnly ? (
          <EmptyState
            icon="🔧"
            title="No maintenance tasks yet"
            message="Track recurring upkeep — filter changes, gutter cleaning, HVAC service — and never miss a due date."
            actionLabel="+ Add Task"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <p className="empty-state">No matching tasks.</p>
        )
      ) : (
        <div className="card-grid">
          {filtered.map((task) => (
            <TaskCard key={task._id} task={task} onEdit={handleEdit} onDetail={setDetailTask} />
          ))}
        </div>
      )}

      {showForm && <TaskForm existing={editingTask} onClose={handleClose} />}
      {detailTask && (
        <TaskDetailModal
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onEdit={(task) => { setDetailTask(null); handleEdit(task); }}
        />
      )}
    </div>
  );
};

export default Tasks;
